import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Wand2, Save, RotateCcw, Loader2, Target, Layers, Tv, Youtube, Wallet, PiggyBank, TrendingUp, Star, BarChart3, Sparkles, Hand, Lock, FolderPlus, Trash2 } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Cell } from "recharts";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { TID } from "@/constants/testIds";
import VendorParametersSelect from "@/components/VendorParametersSelect";
import SaveParameterDialog from "@/components/SaveParameterDialog";
import { fmtCurrency, fmtPercent, fmtNumber, formatNumberWithSeparator } from "@/lib/format";
import { useActiveCampaign } from "@/contexts/ActiveCampaignContext";

const DEFAULTS = {
  total_budget: 0,
  tv_budget: 0,
  olv_budget: 0,
  tv_cpp: 3537000,
  olv_ecpm: 22000,
  ddf: 1.061,
  in_demo: 0.75,
  viewability: 0.85,
  scenario_name: "New Scenario",
};

export default function Optimizer() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { campaigns, activeCampaignId, setActiveCampaignId } = useActiveCampaign();
  const [curves, setCurves] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [campaignId, setCampaignId] = useState(params.get("campaign") || activeCampaignId || "");
  const [curveId, setCurveId] = useState("");
  const [form, setForm] = useState(DEFAULTS);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [savedScenarios, setSavedScenarios] = useState([]);
  const [useForDashboard, setUseForDashboard] = useState(true);
  const [budgetMode, setBudgetMode] = useState("manual"); // 'manual' | 'auto'
  const [optimizeObjective, setOptimizeObjective] = useState("maximize_reach"); // 'maximize_reach' | 'minimize_budget_for_target'
  const [targetCombinedReach, setTargetCombinedReach] = useState("");
  const [optimizeResult, setOptimizeResult] = useState(null); // {best, iterations, params, internal_max_budget?}
  // ---- Phase C: Save Parameter Values dialog state ----
  const [paramDialogOpen, setParamDialogOpen] = useState(false);
  const [saveAsCampaignOpen, setSaveAsCampaignOpen] = useState(false);
  const [newCampaignName, setNewCampaignName] = useState("");
  const pendingSaveRef = useRef(null);
  // Delete-scenario confirmation
  const [scenarioToDelete, setScenarioToDelete] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  // ---- Phase B: Vendor Parameters auto-fill + source tracking ----
  const [vpId, setVpId] = useState("");
  const [vpSource, setVpSource] = useState("manual"); // 'manual' | 'vendor' | 'custom'
  // Watched calculation fields (when these change while source === 'vendor', flip to 'custom')
  const VP_WATCH = ["tv_cpp", "olv_ecpm", "ddf", "in_demo", "viewability"];
  const updateWithVpTracking = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (vpSource === "vendor" && VP_WATCH.includes(k)) setVpSource("custom");
  };
  const onPickVendor = (id, vendor) => {
    setVpId(id);
    if (!vendor) {
      setVpSource("manual");
      return;
    }
    setVpSource("vendor");
    setForm((f) => ({
      ...f,
      tv_cpp: vendor.cpp != null ? vendor.cpp : f.tv_cpp,
      olv_ecpm: vendor.ecpm != null ? vendor.ecpm : f.olv_ecpm,
      in_demo: vendor.in_demo != null ? vendor.in_demo : f.in_demo,
      viewability: vendor.viewability != null ? vendor.viewability : f.viewability,
    }));
  };

  // Phase C helpers
  const buildSnapshot = () => ({
    universe_size: form.universe_size ? Number(form.universe_size) : null,
    tv_cprp: Number(form.tv_cpp) || null,
    cpp: Number(form.tv_cpp) || null,
    olv_ecpm: Number(form.olv_ecpm) || null,
    ddf: Number(form.ddf) || null,
    in_demo: Number(form.in_demo) || null,
    viewability: Number(form.viewability) || null,
  });
  const vpFieldsForSave = () => ({
    vendor_parameter_id: vpId || null,
    vendor_parameter_name: (vendors.find((v) => v.id === vpId) || {}).vendor_name || null,
    parameter_source_type: vpSource,
    parameter_snapshot: buildSnapshot(),
  });

  // ---- Save Scenario orchestration (Phase C dialog gating) ----
  const requestSaveScenario = () => {
    if (vpSource === "manual" || vpSource === "custom") {
      pendingSaveRef.current = { kind: "scenario" };
      setParamDialogOpen(true);
    } else {
      run(true);
    }
  };

  const handleDoNotSaveVP = async () => {
    setParamDialogOpen(false);
    const p = pendingSaveRef.current; pendingSaveRef.current = null;
    if (!p) return;
    if (p.kind === "scenario") await run(true);
    if (p.kind === "save-as-campaign") await createCampaignFromOptimizer({});
  };

  const handleSaveAsNewVP = async (vendorName) => {
    setParamDialogOpen(false);
    const p = pendingSaveRef.current; pendingSaveRef.current = null;
    if (!p) return;
    try {
      const { data: newVp } = await api.post("/vendors", {
        vendor_name: vendorName,
        target_audience: form.target_audience || null,
        universe_size: form.universe_size ? Number(form.universe_size) : null,
        cpp: Number(form.tv_cpp) || null,
        ecpm: Number(form.olv_ecpm) || null,
        in_demo: Number(form.in_demo) || null,
        viewability: Number(form.viewability) || null,
      });
      // Refresh vendors + relink source
      const { data: vs } = await api.get("/vendors");
      setVendors(vs);
      setVpId(newVp.id);
      setVpSource("vendor");
      toast.success(`New Vendor Parameters "${vendorName}" created`);
      if (p.kind === "scenario") await run(true);
      if (p.kind === "save-as-campaign") await createCampaignFromOptimizer({ vpId: newVp.id, vpName: newVp.vendor_name, source: "vendor" });
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleReplaceExistingVP = async () => {
    setParamDialogOpen(false);
    const p = pendingSaveRef.current; pendingSaveRef.current = null;
    if (!p || !vpId) return;
    try {
      const existing = vendors.find((v) => v.id === vpId) || {};
      await api.put(`/vendors/${vpId}`, {
        vendor_name: existing.vendor_name,
        target_audience: existing.target_audience ?? null,
        universe_size: form.universe_size ? Number(form.universe_size) : existing.universe_size ?? null,
        cpp: Number(form.tv_cpp) || existing.cpp || null,
        ecpm: Number(form.olv_ecpm) || existing.ecpm || null,
        affinity: existing.affinity ?? null,
        in_demo: Number(form.in_demo) || existing.in_demo || null,
        viewability: Number(form.viewability) || existing.viewability || null,
      });
      const { data: vs } = await api.get("/vendors");
      setVendors(vs);
      setVpSource("vendor");
      toast.success(`Vendor Parameters "${existing.vendor_name}" updated`);
      if (p.kind === "scenario") await run(true);
      if (p.kind === "save-as-campaign") await createCampaignFromOptimizer({ source: "vendor" });
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleCancelParamDialog = () => {
    setParamDialogOpen(false);
    pendingSaveRef.current = null;
  };

  // ---- Delete Scenario flow ----
  const requestDeleteScenario = (s) => setScenarioToDelete(s);
  const cancelDeleteScenario = () => setScenarioToDelete(null);
  const confirmDeleteScenario = async () => {
    if (!scenarioToDelete) return;
    const s = scenarioToDelete;
    setDeleteBusy(true);
    try {
      await api.delete(`/scenarios/${s.id}`);
      // Optimistic update + authoritative refetch
      setSavedScenarios((prev) => prev.filter((x) => x.id !== s.id));
      if (campaignId) {
        const { data: sc } = await api.get(`/campaigns/${campaignId}/scenarios`);
        setSavedScenarios(sc);
      }
      if (s.is_selected) {
        toast.success("Scenario deleted. Dashboard selection has been reset to the campaign default.");
      } else {
        toast.success("Scenario deleted successfully.");
      }
      setScenarioToDelete(null);
    } catch (err) {
      toast.error(`Failed to delete scenario. ${formatApiError(err) || "Please try again."}`);
    } finally {
      setDeleteBusy(false);
    }
  };

  // ---- Save as Campaign flow ----
  const openSaveAsCampaign = () => {
    if (!result) return;
    setNewCampaignName(form.scenario_name || "Optimizer Scenario");
    setSaveAsCampaignOpen(true);
  };

  const submitSaveAsCampaign = async () => {
    if (!newCampaignName.trim()) {
      toast.error("Campaign name is required.");
      return;
    }
    setSaveAsCampaignOpen(false);
    // If params were customized/manual, run the SaveParameter dialog first
    if (vpSource === "manual" || vpSource === "custom") {
      pendingSaveRef.current = { kind: "save-as-campaign" };
      setParamDialogOpen(true);
      return;
    }
    await createCampaignFromOptimizer({});
  };

  const createCampaignFromOptimizer = async ({ vpId: vpIdOverride, vpName: vpNameOverride, source } = {}) => {
    try {
      const finalVpId = vpIdOverride ?? vpId ?? null;
      const finalVpName = vpNameOverride ?? (vendors.find((v) => v.id === finalVpId) || {}).vendor_name ?? null;
      const finalSource = source ?? vpSource;
      const total = Number(form.total_budget) || 0;
      const tv = Number(form.tv_budget) || 0;
      const olv = Math.max(0, total - tv);
      const payload = {
        campaign_name: newCampaignName.trim(),
        client: null,
        agency: null,
        target_group: form.target_audience || null,
        universe_size: form.universe_size ? Number(form.universe_size) : null,
        total_budget: total,
        tv_budget: tv,
        olv_budget: olv,
        effective_frequency: 1,
        selected_curve_set_id: curveId || null,
        notes: `Created from Optimizer · Combined Reach ${result?.combined_reach?.toFixed?.(2) || "—"}%`,
        status: "active",
        vendor_parameter_id: finalVpId || null,
        vendor_parameter_name: finalVpName,
        parameter_source_type: finalSource,
        parameter_snapshot: buildSnapshot(),
      };
      const { data: created } = await api.post("/campaigns", payload);
      toast.success(`Campaign "${created.campaign_name}" created from optimizer`);
      // Defer navigation by one tick so Radix Dialog finishes unmounting and
      // releasing focus before React Router transitions away.
      setTimeout(() => nav("/campaigns"), 0);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  useEffect(() => {
    (async () => {
      const [{ data: cv }, { data: vs }] = await Promise.all([
        api.get("/curve-sets"),
        api.get("/vendors"),
      ]);
      setCurves(cv);
      setVendors(vs);
    })();
  }, []);

  // When campaign changes, pre-fill form from campaign + vendors
  useEffect(() => {
    if (!campaignId) return;
    const camp = campaigns.find((c) => c.id === campaignId);
    if (!camp) return;
    const tvVendor = vendors.find((v) => v.media_type === "TV");
    const olvVendor = vendors.find((v) => v.media_type === "OLV" || v.media_type === "YouTube");
    setForm({
      scenario_name: `${camp.campaign_name} — Scenario`,
      total_budget: camp.total_budget || 0,
      tv_budget: camp.tv_budget || 0,
      olv_budget: camp.olv_budget || 0,
      tv_cpp: tvVendor?.cpp || DEFAULTS.tv_cpp,
      olv_ecpm: olvVendor?.ecpm || DEFAULTS.olv_ecpm,
      ddf: 1.061,
      in_demo: olvVendor?.in_demo || 0.75,
      viewability: olvVendor?.viewability || 0.85,
    });
    if (camp.selected_curve_set_id) setCurveId(camp.selected_curve_set_id);
    // Load scenarios for this campaign
    api.get(`/campaigns/${campaignId}/scenarios`).then(({ data }) => setSavedScenarios(data)).catch(() => {});
  }, [campaignId, campaigns, vendors]);

  const update = (k, v) => setForm((f) => {
    const next = { ...f, [k]: v };
    // Manual mode: keep OLV in sync with total - tv whenever total or tv changes
    if (budgetMode === "manual" && (k === "total_budget" || k === "tv_budget")) {
      const total = Number(next.total_budget) || 0;
      const tv = Number(next.tv_budget) || 0;
      next.olv_budget = Math.max(0, total - tv);
    }
    return next;
  });

  // When mode changes, keep budgets coherent
  useEffect(() => {
    setForm((f) => {
      if (budgetMode === "manual") {
        const total = Number(f.total_budget) || 0;
        const tv = Number(f.tv_budget) || 0;
        return { ...f, olv_budget: Math.max(0, total - tv) };
      }
      return f;
    });
  }, [budgetMode]);

  const validateBudgets = () => {
    // Target Reach mode: validate target only, skip budget validation
    if (budgetMode === "auto" && optimizeObjective === "minimize_budget_for_target") {
      const tStr = String(targetCombinedReach ?? "").trim();
      if (tStr === "") return "Target Combined Reach is required.";
      const t = Number(targetCombinedReach);
      if (!Number.isFinite(t)) return "Please enter a valid number.";
      if (t <= 0) return "Target Combined Reach must be greater than 0.";
      if (t > 100) return "Target Combined Reach cannot be greater than 100%.";
      if (!(Number(form.tv_cpp) > 0)) return "TV CPRP must be greater than 0.";
      if (!(Number(form.olv_ecpm) > 0)) return "OLV ECPM must be greater than 0.";
      return null;
    }
    const totalStr = String(form.total_budget ?? "").trim();
    if (totalStr === "") return "Total Budget is required.";
    const total = Number(form.total_budget);
    if (!Number.isFinite(total) || total <= 0) return "Total Budget must be greater than 0.";
    if (budgetMode === "manual") {
      const tv = Number(form.tv_budget) || 0;
      if (tv < 0) return "TV Budget cannot be negative.";
      if (tv > total) return "TV Budget cannot be greater than Total Budget.";
    }
    return null;
  };

  const run = async (save = false) => {
    if (!curveId) {
      toast.error("Please select a curve set");
      return;
    }
    const err = validateBudgets();
    if (err) { toast.error(err); return; }

    setBusy(true);
    try {
      if (budgetMode === "auto" && optimizeObjective === "minimize_budget_for_target") {
        // Auto Optimize — Minimize Budget for Target Combined Reach
        const { data: opt } = await api.post("/optimizer/target-reach", {
          curve_set_id: curveId,
          campaign_id: campaignId || null,
          scenario_name: form.scenario_name,
          target_combined_reach: Number(targetCombinedReach),
          tv_cpp: Number(form.tv_cpp),
          olv_ecpm: Number(form.olv_ecpm),
          ddf: Number(form.ddf),
          in_demo: Number(form.in_demo),
          viewability: Number(form.viewability),
        });
        setOptimizeResult(opt);
        // Populate Total/TV/OLV with the recommended values
        setForm((f) => ({
          ...f,
          total_budget: opt.best.total_budget,
          tv_budget: opt.best.tv_budget,
          olv_budget: opt.best.olv_budget,
        }));
        setResult({
          tv_grp: opt.best.tv_grp,
          olv_grp: opt.best.olv_grp,
          olv_impressions: opt.best.olv_impressions,
          tv_reach: opt.best.tv_reach,
          olv_reach: opt.best.olv_reach,
          combined_reach: opt.best.combined_reach,
          effective_cprp: opt.best.effective_cprp,
          saving_vs_tv_only: opt.best.saving_vs_tv_only,
          tv_only_grp_equiv: opt.best.tv_only_grp_equiv,
          tv_only_cost_equiv: opt.best.tv_only_cost_equiv,
        });

        if (save) {
          await api.post("/optimizer/run", {
            curve_set_id: curveId,
            campaign_id: campaignId || null,
            scenario_name: form.scenario_name,
            total_budget: opt.best.total_budget,
            tv_budget: opt.best.tv_budget,
            olv_budget: opt.best.olv_budget,
            tv_cpp: Number(form.tv_cpp),
            olv_ecpm: Number(form.olv_ecpm),
            ddf: Number(form.ddf),
            in_demo: Number(form.in_demo),
            viewability: Number(form.viewability),
            save: true,
            use_for_dashboard: useForDashboard,
            ...vpFieldsForSave(),
          });
          toast.success(useForDashboard ? "Target-reach scenario saved & selected for Dashboard" : "Target-reach scenario saved");
          if (campaignId) {
            const { data: sc } = await api.get(`/campaigns/${campaignId}/scenarios`);
            setSavedScenarios(sc);
          }
        } else {
          toast.success(`Target ${Number(targetCombinedReach).toFixed(2)}% reached at lowest budget across ${opt.count} candidates`);
        }
      } else if (budgetMode === "auto") {
        // Run allocation sweep first
        const { data: opt } = await api.post("/optimizer/optimize-allocation", {
          curve_set_id: curveId,
          campaign_id: campaignId || null,
          scenario_name: form.scenario_name,
          total_budget: Number(form.total_budget),
          tv_cpp: Number(form.tv_cpp),
          olv_ecpm: Number(form.olv_ecpm),
          ddf: Number(form.ddf),
          in_demo: Number(form.in_demo),
          viewability: Number(form.viewability),
        });
        setOptimizeResult(opt);
        // Update displayed TV / OLV from the winner
        setForm((f) => ({
          ...f,
          tv_budget: opt.best.tv_budget,
          olv_budget: opt.best.olv_budget,
        }));
        setResult({
          tv_grp: opt.best.tv_grp,
          olv_grp: opt.best.olv_grp,
          olv_impressions: opt.best.olv_impressions,
          tv_reach: opt.best.tv_reach,
          olv_reach: opt.best.olv_reach,
          combined_reach: opt.best.combined_reach,
          effective_cprp: opt.best.effective_cprp,
          saving_vs_tv_only: opt.best.saving_vs_tv_only,
          tv_only_grp_equiv: opt.best.tv_only_grp_equiv,
          tv_only_cost_equiv: opt.best.tv_only_cost_equiv,
        });

        if (save) {
          // Persist the best as a saved scenario
          await api.post("/optimizer/run", {
            curve_set_id: curveId,
            campaign_id: campaignId || null,
            scenario_name: form.scenario_name,
            total_budget: Number(form.total_budget),
            tv_budget: opt.best.tv_budget,
            olv_budget: opt.best.olv_budget,
            tv_cpp: Number(form.tv_cpp),
            olv_ecpm: Number(form.olv_ecpm),
            ddf: Number(form.ddf),
            in_demo: Number(form.in_demo),
            viewability: Number(form.viewability),
            save: true,
            use_for_dashboard: useForDashboard,
            ...vpFieldsForSave(),
          });
          toast.success(useForDashboard ? "Optimized scenario saved & selected for Dashboard" : "Optimized scenario saved");
          if (campaignId) {
            const { data: sc } = await api.get(`/campaigns/${campaignId}/scenarios`);
            setSavedScenarios(sc);
          }
        } else {
          toast.success(`Auto-optimized across ${opt.count} budget splits`);
        }
      } else {
        // MANUAL: run scenario directly
        setOptimizeResult(null);
        const payload = {
          curve_set_id: curveId,
          campaign_id: campaignId || null,
          scenario_name: form.scenario_name,
          total_budget: Number(form.total_budget),
          tv_budget: Number(form.tv_budget),
          olv_budget: Number(form.olv_budget),
          tv_cpp: Number(form.tv_cpp),
          olv_ecpm: Number(form.olv_ecpm),
          ddf: Number(form.ddf),
          in_demo: Number(form.in_demo),
          viewability: Number(form.viewability),
          save,
          use_for_dashboard: save && useForDashboard,
          ...(save ? vpFieldsForSave() : {}),
        };
        const { data } = await api.post("/optimizer/run", payload);
        setResult(data.result);
        if (save) {
          toast.success(useForDashboard
            ? "Scenario saved and selected for Dashboard"
            : "Scenario saved");
          if (campaignId) {
            const { data: sc } = await api.get(`/campaigns/${campaignId}/scenarios`);
            setSavedScenarios(sc);
          }
        } else {
          toast.success("Scenario calculated");
        }
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const onSelectScenario = async (scenarioId) => {
    try {
      await api.patch(`/scenarios/${scenarioId}/select`);
      toast.success("Scenario selected for Dashboard");
      const { data: sc } = await api.get(`/campaigns/${campaignId}/scenarios`);
      setSavedScenarios(sc);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const onReset = () => { setResult(null); };

  const tvSplitPct = form.total_budget > 0 ? ((form.tv_budget / form.total_budget) * 100).toFixed(1) : "0";

  return (
    <div className="grid grid-cols-1 xl:grid-cols-12 gap-5">
      {/* Inputs */}
      <div className="xl:col-span-4 card-harmony p-5 space-y-4">
        <h3 className="font-display text-lg font-semibold text-slate-900">Scenario Inputs</h3>

        <div>
          <label className="text-xs font-medium uppercase tracking-wider text-slate-500">Campaign</label>
          <Select value={campaignId} onValueChange={(v) => { setCampaignId(v); setActiveCampaignId(v); }}>
            <SelectTrigger className="mt-2" data-testid="opt-campaign-select">
              <SelectValue placeholder="Select campaign…" />
            </SelectTrigger>
            <SelectContent>
              {campaigns.map((c) => (<SelectItem key={c.id} value={c.id}>{c.campaign_name}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="text-xs font-medium uppercase tracking-wider text-slate-500">Curve Set</label>
          <Select value={curveId} onValueChange={setCurveId}>
            <SelectTrigger className="mt-2" data-testid={TID.optCurveSelect}>
              <SelectValue placeholder="Select curve…" />
            </SelectTrigger>
            <SelectContent>
              {curves.map((c) => (<SelectItem key={c.id} value={c.id}>{c.curve_name}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="text-xs font-medium uppercase tracking-wider text-slate-500">Scenario Name</label>
          <input value={form.scenario_name} onChange={(e) => update("scenario_name", e.target.value)}
            className="mt-2 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0E73C9] focus:border-[#0E73C9] outline-none" />
        </div>

        <div>
          <label className="text-xs font-medium uppercase tracking-wider text-slate-500">Budget Allocation Mode</label>
          <div className="mt-2 grid grid-cols-2 gap-1 bg-slate-100 rounded-lg p-1" role="tablist">
            <button
              type="button"
              onClick={() => setBudgetMode("manual")}
              data-testid="opt-mode-manual"
              role="tab"
              aria-selected={budgetMode === "manual"}
              className={`flex items-center justify-center gap-1.5 text-xs font-semibold uppercase tracking-wider py-2 rounded-md transition-all ${
                budgetMode === "manual"
                  ? "bg-white text-[#0A3D91] shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              <Hand className="h-3.5 w-3.5" /> Manual
            </button>
            <button
              type="button"
              onClick={() => setBudgetMode("auto")}
              data-testid="opt-mode-auto"
              role="tab"
              aria-selected={budgetMode === "auto"}
              className={`flex items-center justify-center gap-1.5 text-xs font-semibold uppercase tracking-wider py-2 rounded-md transition-all ${
                budgetMode === "auto"
                  ? "bg-white text-[#0A3D91] shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              <Sparkles className="h-3.5 w-3.5" /> Auto Optimize
            </button>
          </div>
          <div className="text-[11px] text-slate-500 mt-1.5">
            {budgetMode === "manual"
              ? "Enter TV Budget; OLV is calculated as Total − TV."
              : optimizeObjective === "minimize_budget_for_target"
                ? "Enter the desired Combined Reach target. HARMONY will find the lowest budget required to achieve it."
                : "Enter Total Budget only; HARMONY iterates 50–95% TV splits and picks the best Combined Reach."}
          </div>
        </div>

        {budgetMode === "auto" && (
          <div>
            <label className="text-xs font-medium uppercase tracking-wider text-slate-500">Optimization Objective</label>
            <Select value={optimizeObjective} onValueChange={setOptimizeObjective}>
              <SelectTrigger className="mt-2" data-testid="opt-objective-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="maximize_reach">Maximize Combined Reach within Budget</SelectItem>
                <SelectItem value="minimize_budget_for_target">Minimize Budget for Target Combined Reach</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        {budgetMode === "auto" && optimizeObjective === "minimize_budget_for_target" && (
          <div>
            <label className="text-xs font-medium uppercase tracking-wider text-slate-500">Target Combined Reach (%)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={targetCombinedReach}
              onChange={(e) => setTargetCombinedReach(e.target.value)}
              placeholder="e.g. 85 or 85.5"
              data-testid="opt-target-reach"
              className="mt-2 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0E73C9] focus:border-[#0E73C9] outline-none"
            />
            <div className="text-[10px] text-slate-400 mt-1">
              Search limit is automatically calculated from the selected curve range.
            </div>
          </div>
        )}

        {!(budgetMode === "auto" && optimizeObjective === "minimize_budget_for_target") && (
          <NumberField label="Total Budget (IDR)" value={form.total_budget} onChange={(v) => update("total_budget", v)} testId={TID.optTotalBudget} format="integer" />
        )}
        {budgetMode === "auto" && optimizeObjective === "minimize_budget_for_target" && (
          <NumberField
            label="Recommended Total Budget"
            value={form.total_budget}
            onChange={() => {}}
            testId={TID.optTotalBudget}
            readOnly={true}
            hint="filled after Run — lowest budget that achieves Target Combined Reach"
            format="integer"
          />
        )}
        <div className="grid grid-cols-2 gap-3">
          <NumberField
            label={`TV Budget (${tvSplitPct}%)`}
            value={form.tv_budget}
            onChange={(v) => update("tv_budget", v)}
            testId={TID.optTvBudget}
            readOnly={budgetMode === "auto"}
            hint={budgetMode === "auto" ? "filled after Run" : null}
            format="integer"
          />
          <NumberField
            label="OLV Budget"
            value={form.olv_budget}
            onChange={(v) => update("olv_budget", v)}
            testId={TID.optOlvBudget}
            readOnly={true}
            hint={budgetMode === "manual" ? "Automatically calculated from Total Budget minus TV Budget." : "filled after Run"}
            format="integer"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <NumberField label="TV CPRP" value={form.tv_cpp} onChange={(v) => updateWithVpTracking("tv_cpp", v)} testId={TID.optTvCpp} format="integer" />
          <NumberField label="OLV eCPM" value={form.olv_ecpm} onChange={(v) => updateWithVpTracking("olv_ecpm", v)} testId={TID.optOlvEcpm} format="integer" />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <NumberField label="DDF" value={form.ddf} onChange={(v) => updateWithVpTracking("ddf", v)} step="0.001" testId={TID.optDdf} />
          <NumberField label="In-demo" value={form.in_demo} onChange={(v) => updateWithVpTracking("in_demo", v)} step="0.01" testId={TID.optInDemo} />
          <NumberField label="Viewability" value={form.viewability} onChange={(v) => updateWithVpTracking("viewability", v)} step="0.01" testId={TID.optViewability} />
        </div>

        <VendorParametersSelect
          vendors={vendors}
          selectedId={vpId}
          sourceType={vpSource}
          onChange={onPickVendor}
          testId="opt-vp-select"
        />

        <label className="flex items-center gap-2 cursor-pointer pt-2">
          <Checkbox checked={useForDashboard} onCheckedChange={(v) => setUseForDashboard(!!v)} data-testid="opt-use-for-dashboard" />
          <span className="text-sm text-slate-700">
            Use this scenario for Dashboard
          </span>
        </label>

        <div className="flex gap-2 pt-1">
          <button onClick={() => run(false)} disabled={busy} className="btn-primary flex-1 justify-center" data-testid={TID.optRun}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
            Run Scenario
          </button>
          <button onClick={requestSaveScenario} disabled={busy || !result} className="btn-secondary" data-testid={TID.optSave}>
            <Save className="h-4 w-4" /> Save
          </button>
          <button onClick={onReset} className="btn-secondary" data-testid={TID.optReset}>
            <RotateCcw className="h-4 w-4" />
          </button>
        </div>
        {result && (
          <button
            onClick={openSaveAsCampaign}
            className="btn-primary w-full justify-center bg-gradient-to-r from-[#0A3D91] to-[#1CC7B3] hover:opacity-90"
            data-testid="opt-save-as-campaign"
          >
            <FolderPlus className="h-4 w-4" /> Save as Campaign
          </button>
        )}
      </div>

      {/* Results */}
      <div className="xl:col-span-8 space-y-5">
        <div className="card-harmony p-5" data-testid={TID.optResult}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg font-semibold text-slate-900">Scenario Result</h3>
            <span
              data-testid="opt-allocation-mode-label"
              className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full font-semibold ${
                budgetMode === "auto"
                  ? "bg-gradient-to-r from-[#0A3D91]/10 to-[#1CC7B3]/15 text-[#0A3D91]"
                  : "bg-slate-100 text-slate-700"
              }`}
            >
              {budgetMode === "auto"
                ? (<><Sparkles className="h-3 w-3" /> Auto Optimized · {optimizeObjective === "minimize_budget_for_target" ? "Target Reach" : "Max Reach"}</>)
                : (<><Hand className="h-3 w-3" /> Manual Budget</>)}
            </span>
          </div>
          {budgetMode === "auto" && optimizeResult?.best && (
            <div className="mb-4 rounded-lg border border-[#0A3D91]/15 bg-gradient-to-br from-[#0A3D91]/4 to-[#1CC7B3]/8 p-4" data-testid="opt-auto-recommendation">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="h-4 w-4 text-[#0A3D91]" />
                <div className="font-display font-semibold text-sm text-[#0A3D91]">
                  Recommended Allocation
                </div>
                <span className="text-[10px] uppercase tracking-wider text-slate-500 ml-1">
                  {optimizeObjective === "minimize_budget_for_target"
                    ? `target ${Number(optimizeResult.target_combined_reach || targetCombinedReach).toFixed(2)}% • ${optimizeResult.count} candidates`
                    : `best of ${optimizeResult.count} splits`}
                </span>
              </div>
              <div className={`grid grid-cols-2 ${optimizeObjective === "minimize_budget_for_target" ? "md:grid-cols-6" : "md:grid-cols-5"} gap-3 text-sm`}>
                {optimizeObjective === "minimize_budget_for_target" && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">Total Budget</div>
                    <div className="font-display text-base font-bold text-[#0A3D91]" data-testid="opt-rec-total-budget">
                      IDR {fmtCurrency(optimizeResult.best.total_budget)}
                    </div>
                  </div>
                )}
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">TV Budget</div>
                  <div className="font-display text-base font-bold text-[#0A3D91]" data-testid="opt-rec-tv-budget">
                    IDR {fmtCurrency(optimizeResult.best.tv_budget)}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">OLV Budget</div>
                  <div className="font-display text-base font-bold text-[#0E73C9]" data-testid="opt-rec-olv-budget">
                    IDR {fmtCurrency(optimizeResult.best.olv_budget)}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">TV Share</div>
                  <div className="font-display text-base font-bold text-slate-900" data-testid="opt-rec-tv-share">
                    {(optimizeResult.best.tv_share * 100).toFixed(1)}%
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">OLV Share</div>
                  <div className="font-display text-base font-bold text-slate-900">
                    {(optimizeResult.best.olv_share * 100).toFixed(1)}%
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">
                    {optimizeObjective === "minimize_budget_for_target" ? "Achieved Reach" : "Best Combined Reach"}
                  </div>
                  <div className="font-display text-base font-bold text-[#0B8F80]" data-testid="opt-rec-best-reach">
                    {fmtPercent(optimizeResult.best.combined_reach)}
                  </div>
                </div>
              </div>
              {optimizeObjective === "minimize_budget_for_target" && optimizeResult.internal_max_budget && (
                <div className="text-[10px] text-slate-500 mt-2">
                  Search limit auto-calculated from curve range: IDR {fmtCurrency(optimizeResult.internal_max_budget)}
                </div>
              )}
            </div>
          )}
          {!result ? (
            <div className="py-12 text-center text-slate-500 text-sm">
              <Wand2 className="h-8 w-8 mx-auto text-slate-300 mb-3" />
              Configure inputs and click <b>Run Scenario</b> to see results.
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <ResultTile label="TV GRP" value={fmtNumber(result.tv_grp)} icon={Tv} accent="deep" />
              <ResultTile label="OLV Impressions" value={fmtNumber(result.olv_impressions)} icon={Youtube} accent="cyan" />
              <ResultTile label="TV Reach" value={fmtPercent(result.tv_reach)} icon={Target} accent="blue" />
              <ResultTile label="OLV Reach" value={fmtPercent(result.olv_reach)} icon={Target} accent="cyan" />
              <ResultTile label="Combined Reach" value={fmtPercent(result.combined_reach)} icon={Layers} accent="teal" highlight />
              <ResultTile label="Effective CPRP" value={`IDR ${fmtCurrency(result.effective_cprp)}`} icon={Wallet} accent="blue" />
              <ResultTile label="TV-only GRP equiv." value={fmtNumber(result.tv_only_grp_equiv)} icon={TrendingUp} accent="deep" />
              <ResultTile label="Saving vs TV Only" value={`IDR ${fmtCurrency(result.saving_vs_tv_only)}`}
                icon={PiggyBank} accent={result.saving_vs_tv_only >= 0 ? "teal" : "deep"}
                highlight={result.saving_vs_tv_only >= 0} />
            </div>
          )}
        </div>

        {budgetMode === "auto" && optimizeResult?.iterations?.length > 0 && (
          <div className="card-harmony p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display text-lg font-semibold text-slate-900">
                Allocation Iterations
              </h3>
              <span className="text-[10px] uppercase tracking-wider text-slate-400">
                {optimizeResult.count} splits tested
              </span>
            </div>
            <div className="h-56 min-h-[224px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={optimizeResult.iterations} margin={{ top: 10, right: 16, left: -10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                  <XAxis
                    dataKey="tv_share"
                    fontSize={10}
                    stroke="#64748B"
                    tickFormatter={(v) => `${(v * 100).toFixed(0)}%`}
                    interval={4}
                  />
                  <YAxis fontSize={11} stroke="#64748B" tickFormatter={(v) => `${v.toFixed(0)}%`} domain={[(dmin) => Math.max(0, dmin - 1), (dmax) => dmax + 1]} />
                  <Tooltip
                    formatter={(v) => `${v?.toFixed(2)}%`}
                    labelFormatter={(v) => `TV Share ${(v * 100).toFixed(0)}%`}
                    contentStyle={{ fontSize: 12 }}
                  />
                  <Bar dataKey="combined_reach" name="Combined Reach" radius={[3, 3, 0, 0]}>
                    {optimizeResult.iterations.map((it) => (
                      <Cell
                        key={`bar-${it.tv_share}`}
                        fill={
                          Math.abs(it.tv_share - optimizeResult.best.tv_share) < 1e-6
                            ? "#0A3D91"
                            : "#94A3B8"
                        }
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="overflow-x-auto mt-3 max-h-72 overflow-y-auto" data-testid="opt-iterations-table">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left py-2 px-3"></th>
                    <th className="text-right py-2 px-3">TV Share</th>
                    <th className="text-right py-2 px-3">TV Budget</th>
                    <th className="text-right py-2 px-3">OLV Budget</th>
                    <th className="text-right py-2 px-3">TV Reach</th>
                    <th className="text-right py-2 px-3">OLV Reach</th>
                    <th className="text-right py-2 px-3">Combined</th>
                    <th className="text-right py-2 px-3">Eff. CPRP</th>
                  </tr>
                </thead>
                <tbody>
                  {optimizeResult.iterations.map((it) => {
                    const isBest = Math.abs(it.tv_share - optimizeResult.best.tv_share) < 1e-6;
                    return (
                      <tr key={`iter-${it.tv_share}`} className={`border-t border-slate-100 ${isBest ? "bg-amber-50/60" : "hover:bg-slate-50"}`}>
                        <td className="py-1.5 px-3">
                          {isBest && <Star className="h-3 w-3 text-amber-500 fill-amber-500" />}
                        </td>
                        <td className="py-1.5 px-3 text-right font-medium">{(it.tv_share * 100).toFixed(0)}%</td>
                        <td className="py-1.5 px-3 text-right">{fmtCurrency(it.tv_budget)}</td>
                        <td className="py-1.5 px-3 text-right">{fmtCurrency(it.olv_budget)}</td>
                        <td className="py-1.5 px-3 text-right">{fmtPercent(it.tv_reach)}</td>
                        <td className="py-1.5 px-3 text-right">{fmtPercent(it.olv_reach)}</td>
                        <td className={`py-1.5 px-3 text-right font-semibold ${isBest ? "text-[#0B8F80]" : ""}`}>{fmtPercent(it.combined_reach)}</td>
                        <td className="py-1.5 px-3 text-right">{fmtCurrency(it.effective_cprp)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="card-harmony p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display text-lg font-semibold text-slate-900">
              Scenarios {campaignId ? "for this campaign" : ""}
            </h3>
            {campaignId && (
              <button onClick={() => nav("/")} className="text-xs font-medium text-[#0E73C9] hover:underline">
                Open Dashboard →
              </button>
            )}
          </div>
          {savedScenarios.length === 0 ? (
            <div className="text-sm text-slate-500 py-8 text-center">No saved scenarios yet for this campaign.</div>
          ) : (
            <>
              {/* Overlay comparison chart */}
              <ScenarioComparisonChart scenarios={savedScenarios} liveResult={result} scenarioName={form.scenario_name} />

              <div className="overflow-x-auto mt-4">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
                  <tr>
                    <th className="text-left py-2 px-3"></th>
                    <th className="text-left py-2 px-3">Scenario</th>
                    <th className="text-right py-2 px-3">TV Budget</th>
                    <th className="text-right py-2 px-3">OLV Budget</th>
                    <th className="text-right py-2 px-3">TV GRP</th>
                    <th className="text-right py-2 px-3">TV Reach</th>
                    <th className="text-right py-2 px-3">OLV Reach</th>
                    <th className="text-right py-2 px-3">Combined</th>
                    <th className="text-right py-2 px-3">CPRP</th>
                    <th className="text-right py-2 px-3">Saving</th>
                    <th className="text-right py-2 px-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {savedScenarios.map((s) => (
                    <tr key={s.id} className={`border-t border-slate-100 hover:bg-slate-50 ${s.is_selected ? "bg-amber-50/40" : ""}`}>
                      <td className="py-2 px-3">
                        {s.is_selected && <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-500" />}
                      </td>
                      <td className="py-2 px-3 font-medium text-slate-900">{s.scenario_name}</td>
                      <td className="py-2 px-3 text-right">{fmtCurrency(s.tv_budget)}</td>
                      <td className="py-2 px-3 text-right">{fmtCurrency(s.olv_budget)}</td>
                      <td className="py-2 px-3 text-right">{fmtNumber(s.tv_grp)}</td>
                      <td className="py-2 px-3 text-right">{fmtPercent(s.tv_reach)}</td>
                      <td className="py-2 px-3 text-right">{fmtPercent(s.olv_reach)}</td>
                      <td className="py-2 px-3 text-right font-semibold text-[#0B8F80]">{fmtPercent(s.combined_reach)}</td>
                      <td className="py-2 px-3 text-right">{fmtCurrency(s.effective_cprp)}</td>
                      <td className="py-2 px-3 text-right text-emerald-600 font-semibold">{fmtCurrency(s.saving_vs_tv_only)}</td>
                      <td className="py-2 px-3 text-right">
                        <div className="flex items-center justify-end gap-3">
                          {!s.is_selected && (
                            <button onClick={() => onSelectScenario(s.id)}
                              className="text-xs text-[#0E73C9] hover:text-[#0A3D91] font-medium"
                              data-testid={`scenario-select-${s.id}`}>
                              Use for Dashboard
                            </button>
                          )}
                          <button
                            onClick={() => requestDeleteScenario(s)}
                            className="text-rose-600 hover:text-rose-700 p-1 rounded hover:bg-rose-50 transition-colors"
                            title="Delete scenario"
                            data-testid={`scenario-delete-${s.id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </>
          )}
        </div>
      </div>

      <SaveParameterDialog
        open={paramDialogOpen}
        sourceType={vpSource}
        selectedVendorName={(vendors.find((v) => v.id === vpId) || {}).vendor_name}
        snapshot={buildSnapshot()}
        onDoNotSave={handleDoNotSaveVP}
        onSaveAsNew={handleSaveAsNewVP}
        onReplaceExisting={handleReplaceExistingVP}
        onCancel={handleCancelParamDialog}
      />

      <Dialog open={saveAsCampaignOpen} onOpenChange={setSaveAsCampaignOpen}>
        <DialogContent className="max-w-md" data-testid="save-as-campaign-dialog">
          <DialogHeader><DialogTitle>Save as Campaign</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Create a new Campaign pre-filled with the current optimizer result. You can edit any field afterwards in the Campaigns menu.</p>
          <div>
            <label className="text-xs font-medium uppercase tracking-wider text-slate-500">Campaign Name *</label>
            <input
              type="text"
              value={newCampaignName}
              onChange={(e) => setNewCampaignName(e.target.value)}
              data-testid="save-as-campaign-name"
              autoFocus
              className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0E73C9] focus:border-[#0E73C9] outline-none"
            />
          </div>
          <div className="rounded-lg bg-slate-50 p-3 text-xs space-y-1">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium mb-1">Will be created with</div>
            <div className="flex justify-between"><span className="text-slate-500">Total Budget</span><span className="font-medium">{fmtCurrency(form.total_budget)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">TV Budget</span><span className="font-medium">{fmtCurrency(form.tv_budget)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">OLV Budget</span><span className="font-medium">{fmtCurrency(form.olv_budget)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Combined Reach</span><span className="font-medium text-[#0B8F80]">{result?.combined_reach != null ? fmtPercent(result.combined_reach) : "—"}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Parameter Source</span><span className="font-medium capitalize">{vpSource}</span></div>
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t border-slate-100 mt-3">
            <button onClick={() => setSaveAsCampaignOpen(false)} className="btn-secondary">Cancel</button>
            <button onClick={submitSaveAsCampaign} className="btn-primary" data-testid="save-as-campaign-submit">
              <FolderPlus className="h-4 w-4" /> Create Campaign
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!scenarioToDelete} onOpenChange={(o) => { if (!o) cancelDeleteScenario(); }}>
        <DialogContent className="max-w-md" data-testid="scenario-delete-dialog">
          <DialogHeader>
            <DialogTitle>Delete Scenario?</DialogTitle>
            <DialogDescription>
              {scenarioToDelete?.scenario_name
                ? <>Are you sure you want to delete &quot;{scenarioToDelete.scenario_name}&quot;? This action cannot be undone.</>
                : "Are you sure you want to delete this scenario? This action cannot be undone."}
            </DialogDescription>
          </DialogHeader>
          {scenarioToDelete?.is_selected && (
            <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2">
              This scenario is currently used for Dashboard. Deleting will reset the Dashboard to the campaign default.
            </div>
          )}
          <DialogFooter>
            <button onClick={cancelDeleteScenario} className="btn-secondary" data-testid="scenario-delete-cancel">Cancel</button>
            <button
              onClick={confirmDeleteScenario}
              disabled={deleteBusy}
              className="btn-primary bg-rose-600 hover:bg-rose-700"
              data-testid="scenario-delete-confirm"
            >
              {deleteBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Delete Scenario
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ScenarioComparisonChart({ scenarios, liveResult, scenarioName }) {
  const data = scenarios.slice().reverse().map((s) => ({
    name: s.is_selected ? `★ ${s.scenario_name}` : s.scenario_name,
    tv_reach: s.tv_reach,
    olv_reach: s.olv_reach,
    combined_reach: s.combined_reach,
  }));
  if (liveResult) {
    const liveName = `${scenarioName || "Current"} (live)`;
    if (!data.some((d) => d.name === liveName)) {
      data.push({
        name: liveName,
        tv_reach: liveResult.tv_reach,
        olv_reach: liveResult.olv_reach,
        combined_reach: liveResult.combined_reach,
      });
    }
  }
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-4">
      <div className="flex items-center gap-2 mb-2">
        <BarChart3 className="h-4 w-4 text-[#0A3D91]" />
        <h4 className="font-display font-semibold text-sm text-slate-900">Reach Comparison Overlay</h4>
        <span className="text-[10px] uppercase tracking-wider text-slate-400 ml-1">
          {data.length} scenario{data.length === 1 ? "" : "s"}
        </span>
      </div>
      <div className="h-64 min-h-[256px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 12, right: 16, left: -10, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
            <XAxis dataKey="name" fontSize={10} stroke="#64748B" interval={0}
              angle={data.length > 4 ? -15 : 0}
              textAnchor={data.length > 4 ? "end" : "middle"}
              height={data.length > 4 ? 60 : 30} />
            <YAxis fontSize={11} stroke="#64748B" tickFormatter={(v) => `${v.toFixed(0)}%`} />
            <Tooltip formatter={(v) => `${v?.toFixed(2)}%`} contentStyle={{ fontSize: 12 }} />
            <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="tv_reach" name="TV Reach" fill="#0A3D91" radius={[4, 4, 0, 0]} />
            <Bar dataKey="olv_reach" name="OLV Reach" fill="#18B7D8" radius={[4, 4, 0, 0]} />
            <Bar dataKey="combined_reach" name="Combined Reach" fill="#1CC7B3" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function NumberField({ label, value, onChange, step, testId, readOnly, hint, format }) {
  const isInteger = format === "integer";
  const handleChange = (e) => {
    const v = e.target.value;
    if (isInteger) {
      onChange(v.replace(/[^\d]/g, ""));
    } else {
      onChange(v);
    }
  };
  const displayValue = isInteger ? formatNumberWithSeparator(value) : value;
  return (
    <div>
      <label className="text-xs font-medium uppercase tracking-wider text-slate-500 flex items-center gap-1">
        {label}
        {readOnly && <Lock className="h-3 w-3 text-slate-400" />}
      </label>
      <input
        type={isInteger ? "text" : "number"}
        inputMode={isInteger ? "numeric" : undefined}
        autoComplete="off"
        value={displayValue ?? ""}
        step={!isInteger ? (step || "1") : undefined}
        readOnly={readOnly}
        onChange={handleChange}
        data-testid={testId}
        className={`mt-2 w-full border rounded-lg px-3 py-2 text-sm outline-none ${
          readOnly
            ? "border-slate-200 bg-slate-50 text-slate-600 cursor-not-allowed"
            : "border-slate-200 focus:ring-2 focus:ring-[#0E73C9] focus:border-[#0E73C9]"
        }`}
      />
      {hint && <div className="text-[10px] text-slate-400 mt-1">{hint}</div>}
    </div>
  );
}

function ResultTile({ label, value, icon: Icon, accent, highlight }) {
  const bgMap = {
    deep: "bg-[#0A3D91]/8 text-[#0A3D91]",
    blue: "bg-[#0E73C9]/10 text-[#0E73C9]",
    cyan: "bg-[#18B7D8]/12 text-[#0E73C9]",
    teal: "bg-[#1CC7B3]/15 text-[#0B8F80]",
  };
  return (
    <div className={`rounded-lg p-3 border border-slate-200 ${highlight ? "bg-gradient-to-br from-[#1CC7B3]/8 to-[#18B7D8]/5" : "bg-white"}`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
        <div className={`h-7 w-7 rounded-md flex items-center justify-center ${bgMap[accent]}`}>
          <Icon className="h-3.5 w-3.5" />
        </div>
      </div>
      <div className="font-display text-lg font-bold text-slate-900 mt-1.5 leading-tight">{value}</div>
    </div>
  );
}
